/****************************************************
 * Module Name:  directStoneWiseBidPreview.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { Box, Tooltip, Typography, ThemeProvider, createTheme, Stack, Button, Badge } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import { LoadingButton } from "@mui/lab";
import Constants from "../../../utility/constants";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import CommonSnackbar from "../../common/commonSnackBar";
import { IoIosAddCircleOutline } from "react-icons/io";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import ViewImageModal from "./viewImageModal";
import AddTenderStoneRemarkModal from "./addTenderStoneRemarkModal";
import UploadTenderImagesModal from "./uploadTenderImagesModal";
import EditIcon from '@mui/icons-material/Edit';
import { IoIosLock } from "react-icons/io";
import logo from '../../../images/pansuriya.jpg'
import { Modal } from "react-bootstrap";
import PageTitle from "../../common/pageTitle";

const DirectStoneWiseBidPreview = () => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [companyTenderOptions, setCompanyTenderOptions] = useState([]);
    const [companyTender, setCompanyTender] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [salesBidLoader, setSalesBidLoader] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [outerLoader, setOuterLoader] = useState();
    const [outerRows, setOuterRows] = useState([]);

    const [tenderStoneId, setTenderStoneId] = useState("");
    const [filterValues, setFilterValues] = useState({});
    const [CompanyDataLoader, setCompanyDataLoader] = useState(true);
    const [remark, setRemark] = useState("");
    const [showUploadImgsModal, setShowUploadImgsModal] = useState(false);
    const [showAddRemarkModal, setShowAddRemarkModal] = useState(false);
    const [showViewImageModal, setShowViewImageModal] = useState(false);
    const [loader, setLoader] = useState(false);
    const [innerSelectionModel, setInnerSelectionModel] = useState(new Map());
    const [columnNames, setColumnNames] = React.useState([]);

    const [lotData, setLotData] = React.useState({});
    const [showStoneDataModal, setShowStoneDataModal] = React.useState(false); // to show data after view button clicked

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_STONE);

    useEffect(() => {
        PageTitle("TENDER_OPERATION_BID_PREVIEW_DIRECTSTONE");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (Utility.isReadAllowed(local_Permission)) {
            getCompanyTender();
        }
    }

    const getCompanyTender = () => {

        setOuterRows([]);
        setCompanyDataLoader(true);
        setCompanyTenderOptions([]);
        setCompanyTender("");
        let optionsArray = [];
        TenderLotProxy.GetCompanyTendersForDirectStoneBid(true, "LOCKED", "IN_PROGRESS", (response) => {
            if (response) {
                response.map((data) => {
                    let option = { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                    optionsArray.push(option);
                    return true;
                });
                setCompanyDataLoader(false);
            } else {
                setCompanyDataLoader(false);
            }
        })
        setCompanyTenderOptions(optionsArray);
    }

    const handleSearch = () => {
        setSalesBidLoader(true);
        setOuterRows([]);
        // need to fetch such companies and tenders which are not yet locked 
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"
        setInnerSelectionModel(new Map());
        TenderLotProxy.TenderLotDataDirectStoneBid(companyTender.value, companyTender.companyid, true, "LOCKED", "IN_PROGRESS", (response) => {
            if (response && Array.isArray(response)) {


                const updatedresponse = response.map(iData => ({
                    ...iData,
                    tenderstonelist: iData.tenderstonelist.map(iRow => ({
                        ...iRow,
                        ppc: 0.00,
                    }))
                }));

                const uniqueFilterValues = {
                    tendername: Utility.getDistinctOptions(response, "tendername"),
                    minename: Utility.getDistinctOptions(response, "minename"),
                };

                prepareColumns(uniqueFilterValues);
                setFilterValues(uniqueFilterValues);
                setSalesBidLoader(false);
                setOuterLoader(false);
                setOuterRows(updatedresponse);
            } else {
                setSalesBidLoader(false);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "tenderlotid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_LOTCODE];
                    break;
                case "mineid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_MINE_NAME];
                    break;
                case "tendername":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_AMOUNT
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const prepareInnerColumns = (uniqueFilterValues) => {
        const columnNames = innercolumnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "size":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_STONE_SIZE_PERCENT, Constants.COLUMN_PERMISSION.MFG_STONE_PIECES];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, innercolumnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        return columnNames;
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const lockStoneWiseBid = () => {

        let data = [];

        setLoader(true);
        outerRows.forEach(iData => {
            if (innerSelectionModel.has(iData.tenderlotid)) {


                innerSelectionModel.get(iData.tenderlotid).forEach(stoneid => {

                    let stone = iData.tenderstonelist.find(iRow => iRow.id === stoneid);;
                    data.push({
                        tenderstoneid: stoneid,
                        tenderlotid: iData.tenderlotid,
                        priceplanid: [],
                        lockedby: Constants.loggedInUser.id,
                        totalprice: stone.amount,
                        planid: null,  // no plan is locked here
                    });
                });
            }
        });

        TenderLotBidProxy.LockStoneWiseBid(data, (response) => {
            if (response && response.status === "Success") {
                setShowSnackbar(true);
                setSnackbarMessage("Locked Successfully");
                setSnackbarVariant("success");
                handleSearch();
                setLoader(false);
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
            }
        })
    }

    const addRemark = (data) => {
        setLoader(true);
        TenderLotProxy.AddRemark(data, tenderStoneId, (response) => {
            if (response) {
                setShowSnackbar(true);
                setSnackbarMessage("Remark Added Successfully");
                setSnackbarVariant("success");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
            }
        });
    }

    const handleUpload = (data, tenderstoneid) => {
        setLoader(true);
        TenderLotProxy.UploadTenderStoneImages(data, tenderstoneid, (response) => {
            if (response.status === "Success") {
                setShowSnackbar(true);
                setSnackbarMessage(response.errorMessage)
                setSnackbarVariant("success");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            }
        })
    }

    const columnsWithPermission = {
        "eabe76e1-e41b-40ef-b9ae-cbd13293bd1e": {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 78,
            renderCell: renderCellExpand,
            filterOperators: Utility.defaultFilters(),
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "minename",
            headerName: "MINE NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3634473f-45cf-4def-8e69-c1f34ebe0f18": {
            field: "basicprice",
            headerName: "BASIC PRICE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "394908cf-dc48-40ee-b68f-23efa651806e": {
            field: "basicamount",
            headerName: "BASIC AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
    };

    const columnsWithoutPermission = [
        {
            field: "",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: (params) => (
                <Button
                    sx={{
                        width: "44px !important",
                        height: "28px !important",
                        fontSize: "10px !important",
                        borderRadius: "12px !important",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowStoneDataModal(true);
                        setLotData(params.row)
                    }}
                    component="label"
                    variant="contained"
                    size="small"
                >
                    VIEW
                </Button >
            ),
        },
        {
            field: "tenderlotid",
            headerName: "TENDER LOT ID",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mineid",
            headerName: "MINE ID",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tenderid",
            headerName: "TENDER ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tendername",
            headerName: "TENDER",
            headerAlign: "center",
            align: "center",
            width: 212,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => (params.value).toFixed(2),
            renderCell: (params) => (params.value).toFixed(2),
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            headerAlign: "center",
            align: "center",
            width: 212,
            renderCell: renderCellExpand,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 200,
            renderCell: renderCellExpand,
        },
        {
            field: "pcs",
            headerName: "PCS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    ];

    const innercolumnsWithPermission = {
        "86504376-84c2-4f7c-9187-bdd52e129f79": {
            field: "stonesizepercent",
            headerName: "STONE SIZE %",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "9c0b6a70-9b92-4283-b27c-4e424c5d35ef": {
            field: "pieces",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    };

    const innercolumnsWithoutPermission = [
        {
            field: "id",
            headerName: "TENDER STONE ID",
            headerAlign: "center",
            align: "center",
            width: 138,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : 0.00}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
            width: 120
        },
        {
            field: 'amount',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : 0.00}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
            width: 120
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "addremark",
            headerName: "ADD REMARK",
            headerAlign: "center",
            align: "center",
            width: 122,
            renderCell: (params) => {
                return (
                    <Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<IoIosAddCircleOutline size={20} />}
                        onClick={() => {
                            setShowAddRemarkModal(true);
                            setTenderStoneId(params.row.id);
                            setRemark(params.row.remark)
                        }}
                    >
                        REMARK
                    </Button>
                )
            },
        },
        {
            field: "upload",
            headerName: "UPLOAD",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setTenderStoneId(params.row.id);
                            setShowUploadImgsModal(true);
                        }}
                    >
                        {" "}
                        <MdOutlineAddPhotoAlternate color="primary" size={25} />
                    </Box>
                )
            },
        },
        {
            field: "noofimages",
            headerName: "VIEW IMG",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => {
                return params.row.noofimages && params.row.noofimages > 0 ? (
                    <Badge
                        badgeContent={params.row.noofimages}
                        color="primary"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                width: "15px",
                                height: "17px",
                                borderRadius: "50%",
                            }
                        }}
                    >
                        <VideoLibraryTwoToneIcon
                            sx={{ cursor: "pointer", fontSize: "25px" }}
                            onClick={() => {
                                setShowViewImageModal(true);
                                setTenderStoneId(params.row.id);
                            }}
                        />
                    </Badge>
                ) : (
                    <span style={{ color: "black", fontStyle: "italic" }}>No Media</span>
                );
            },
        }
    ];

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.id,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        console.error(error);
    };

    const processRowUpdate = (newR, oldRow, rowsx,) => {
        const changedFeilds = getChangedFields(newR, oldRow);

        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            let newRow = { ...oldRow };

            const updatedRow = rowsx.map(iRow => {
                const updatedTenderstonelist = iRow.tenderstonelist.map(row => {
                    if (row.id === id) {
                        if (field === "amount" && value && parseFloat(value) > 0 && parseFloat(value) !== parseFloat(row.amount)) {
                            const amount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                            const newRow = {
                                ...row,
                                [field]: amount,
                                ppc: parseFloat((amount / row.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS)),
                            };

                            return newRow;
                        } else if (field === "ppc" && value && parseFloat(value) > 0 && parseFloat(value) !== parseFloat(row.ppc)) {
                            const ppc = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                            const newRow = {
                                ...row,
                                [field]: ppc,
                                amount: parseFloat((ppc * row.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS)),
                            };

                            return newRow;
                        } else {
                            setShowSnackbar(true);
                            setSnackbarMessage("Incorrect Data");
                            setSnackbarVariant("warning");

                            return row;
                        }
                    }
                    return row;
                });

                return {
                    ...iRow,
                    tenderstonelist: updatedTenderstonelist,
                };
            });

            setOuterRows(updatedRow);
            return newRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newR };
        }
    }

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row, innerSelectionModel1, mainData) => <DetailPanelContent row={row} mainData={mainData} innerSelectionModel1={innerSelectionModel1} />,
        [],);

    function DetailPanelContent({ row: rowProp, innerSelectionModel1, mainData, isModalView }) {

        let middlerows = [];
        const foundItem = mainData.find((idata) => {
            return rowProp.tenderlotid === idata.tenderlotid;
        });

        if (foundItem && foundItem.tenderstonelist) {
            middlerows = foundItem.tenderstonelist;
        }

        let sortedrows = [...middlerows];
        if (sortedrows.length > 0) {
            sortedrows = sortedrows.sort(
                (p1, p2) => (p1.id > p2.id) ? 1 : (p1.id < p2.id) ? -1 : 0);
        }

        let middlecolumns = prepareInnerColumns(filterValues);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "60vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: isModalView ? '60vh' : "auto",
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middlecolumns}
                        rows={sortedrows}
                        getRowId={(row) => row.id}
                        loading={false} // no need as we have that data already loaded
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: isModalView ? '90vh' : '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setInnerSelectionModel((prevSelection) => {
                                const updatedSelection = new Map(prevSelection);
                                if (newSelectionModel.length === 0) {
                                    updatedSelection.delete(rowProp.tenderlotid);
                                } else {
                                    updatedSelection.set(rowProp.tenderlotid, newSelectionModel);
                                }
                                return updatedSelection;
                            });
                        }}
                        rowSelectionModel={innerSelectionModel1.get(rowProp.tenderlotid) || []}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow, mainData)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "3px",
                            right: "265px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "& .MuiButtonBase-root": {
                                minHeight: "30px",
                            },
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        {isModalView && (
                            <LoadingButton
                                sx={{ mr: 1 }}
                                variant="contained"
                                color="info"
                                onClick={(e) => lockStoneWiseBid()}
                                loading={salesBidLoader}
                                loadingPosition="start"
                                startIcon={<BiSearchAlt2 />}
                                type="Submit"
                                disabled={!companyTender}
                            >
                                Lock
                            </LoadingButton>
                        )}
                    </Box>
                </Box>
            </Stack>
        );
    }

    const defaultMaterialTheme = createTheme();

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            {showSnackbar ? (
                <CommonSnackbar
                    show={true}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
            ) : null}

            {/* shows stone data of the lot which can also be accessed by "+" button */}
            {showStoneDataModal ? (
                <Modal
                    backdrop="static"
                    show={true}
                    onHide={() => setShowStoneDataModal(false)}
                    dialogClassName="max-modalSize-mdx"
                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Lot Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DetailPanelContent
                            row={lotData}
                            mainData={outerRows}
                            innerSelectionModel1={innerSelectionModel}
                            isModalView={true} // this is used to show lock button inside the modal
                        />
                    </Modal.Body>
                </Modal>)
                : null}

            {showUploadImgsModal ? (
                <UploadTenderImagesModal
                    show={showUploadImgsModal}
                    loader={loader}
                    handleSave={handleUpload}
                    handleClose={() => {
                        setShowUploadImgsModal(false);
                        setTenderStoneId();
                    }}
                    tenderstoneid={tenderStoneId}
                />
            ) : null}

            {showAddRemarkModal ? (
                <>
                    <AddTenderStoneRemarkModal
                        show={showAddRemarkModal}
                        remarkData={remark}
                        handleClose={() => setShowAddRemarkModal(false)}
                        handleSave={addRemark}
                    />
                </>
            ) : null}

            {showViewImageModal ? (
                <>
                    <ViewImageModal
                        show={showViewImageModal}
                        tenderstoneid={tenderStoneId}
                        handleClose={() => setShowViewImageModal(false)}
                    />
                </>
            ) : null}


            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>

                <Box sx={{ width: "46%", display: "flex", alignItems: "center" }}>
                    {/* Company Tender */}
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "75%", paddingRight: "15px" }}>
                        <Typography sx={{ width: "25%" }}>Company Tender</Typography>
                        <Select menuPlacement="auto"
                            blurInputOnSelect={false}
                            menuPortalTarget={document.body}
                            styles={{
                                ...Constants.Styles,
                                control: (base) => ({
                                    ...base,
                                    width: '27rem', // Adjust the width as needed
                                    minHeight: "40px"
                                }),
                            }}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            options={companyTenderOptions}
                            value={companyTender}
                            isClearable={true}
                            onChange={(e) => setCompanyTender(e)}
                        />
                        {CompanyDataLoader ?
                            (<Box sx={{ marginLeft: 2 }}>
                                {<CircularProgress size={20} />}
                            </Box>) :
                            (<Box sx={{ marginLeft: 2 }}>
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => getCompanyTender()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            </Box>)}
                    </Box>

                    {/* Submit */}
                    <Box sx={{
                        width: "17%",
                        "& .MuiButtonBase-root": {
                            minHeight: "40px",
                        },
                        "& .Mui-disabled": {
                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                            color: "rgba(0, 0, 0, 0.26) !important"
                        }
                    }}>
                        <LoadingButton
                            sx={{ mr: 1 }}
                            variant="contained"
                            color="info"
                            onClick={handleSearch}
                            loading={salesBidLoader}
                            loadingPosition="start"
                            startIcon={<BiSearchAlt2 />}
                            type="Submit"
                            disabled={!companyTender}
                        >
                            Search
                        </LoadingButton>
                    </Box>
                </Box>

            </Box>

            <ThemeProvider theme={defaultMaterialTheme}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: "70vh",
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        }
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={columnNames}
                        rows={outerRows}
                        loading={outerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        getRowId={(row) => row.tenderlotid}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    tenderlotid: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false,
                                    tenderid: false,
                                    mineid: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={outerRows.length === 0 ? null : ({ row }) => getDetailPanelContent(row, innerSelectionModel, outerRows)}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "2px",
                            right: "265px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "& .MuiButtonBase-root": {
                                minHeight: "30px",
                            },
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        {outerLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}

                        <Box sx={{
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            },
                        }}>
                            <LoadingButton
                                sx={{ mr: 1, ml: 1 }}
                                variant="contained"
                                color="info"
                                onClick={(e) => lockStoneWiseBid()}
                                loading={loader}
                                loadingPosition="start"
                                startIcon={<IoIosLock />}
                                type="Submit"
                                disabled={innerSelectionModel.size === 0}
                            >
                                LOCK
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>

        </Box>
    )
}

export default DirectStoneWiseBidPreview;